import { useMemo, useState } from 'react';
import { buildCentralDayRange } from '../utils/timezone';

const ALL_PROGRAMS = 'All Programs';
const STATUS_OPTIONS = ['sent', 'manual', 'failed', 'skipped', 'dry_run'];

const formatDateInput = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const buildFilterRow = () => ({
  id: `filter-${Math.random().toString(36).slice(2)}`,
  key: '',
  op: 'contains',
  value: '',
});

const normalizeFilters = (rows) =>
  rows
    .map((row) => ({
      key: String(row.key || '').trim(),
      op: String(row.op || 'contains').trim(),
      value: row.value,
    }))
    .filter((row) => row.key && (row.op === 'exists' || row.op === 'not_exists' || String(row.value || '').trim()));

const useEmailSendReportState = () => {
  const today = useMemo(() => new Date(), []);
  const [programName, setProgramName] = useState(ALL_PROGRAMS);
  const [status, setStatus] = useState('');
  const [bac, setBac] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(formatDateInput(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState(formatDateInput(today));
  const [filterRows, setFilterRows] = useState([buildFilterRow()]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [selectedId, setSelectedId] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, id: null });
  const [retryState, setRetryState] = useState({ open: false, id: null });

  const centralRange = useMemo(
    () => buildCentralDayRange(startDate, endDate),
    [startDate, endDate]
  );

  const queryPayload = useMemo(() => ({
    programName: programName === ALL_PROGRAMS ? null : programName,
    status: status || null,
    bac: bac || null,
    search: search || null,
    start: centralRange.start,
    end: centralRange.end,
    filters: appliedFilters,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
  }), [programName, status, bac, search, centralRange, appliedFilters, paginationModel]);

  const applyFilters = () => {
    setAppliedFilters(normalizeFilters(filterRows));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilterRows([buildFilterRow()]);
    setAppliedFilters([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const updateFilterRow = (id, key, value) => {
    setFilterRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  };

  const removeFilterRow = (id) => {
    setFilterRows((prev) => {
      const next = prev.filter((row) => row.id !== id);
      return next.length ? next : [buildFilterRow()];
    });
  };

  return {
    constants: {
      ALL_PROGRAMS,
      STATUS_OPTIONS,
    },
    state: {
      programName,
      status,
      bac,
      search,
      startDate,
      endDate,
      filterRows,
      paginationModel,
      selectedId,
      confirmState,
      retryState,
    },
    setters: {
      setProgramName,
      setStatus,
      setBac,
      setSearch,
      setStartDate,
      setEndDate,
      setFilterRows,
      setPaginationModel,
      setSelectedId,
      setConfirmState,
      setRetryState,
    },
    derived: {
      centralRange,
      queryPayload,
    },
    actions: {
      applyFilters,
      clearFilters,
      updateFilterRow,
      removeFilterRow,
      buildFilterRow,
    },
  };
};

export default useEmailSendReportState;
